
import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plane } from 'lucide-react';
import { BlogPostFormFields } from './form/BlogPostFormFields';
import { BlogPostEditor } from './editor/BlogPostEditor';
import { BlogFormData } from './types/blog';

interface AddBlogPostDialogProps {
  children: React.ReactNode;
  post?: {
    id: string;
    title: string;
    content: string;
    city?: string;
    guide_type?: string;
    tags?: string[];
  };
}

export const AddBlogPostDialog = ({ children, post }: AddBlogPostDialogProps) => {
  const [open, setOpen] = React.useState(false);
  const { register, handleSubmit, reset, watch, setValue } = useForm<BlogFormData>({
    defaultValues: {
      title: post?.title || '',
      content: post?.content || '',
      city: post?.city || '',
      guide_type: post?.guide_type || '',
      tags: post?.tags || [],
    }
  });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = React.useState('edit');

  const { mutate: saveBlogPost, isPending } = useMutation({
    mutationFn: async (data: BlogFormData) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      if (post?.id) {
        // Update existing post
        const { error } = await supabase
          .from('community_posts')
          .update({
            title: data.title,
            content: data.content,
            city: data.city,
            guide_type: data.guide_type,
            tags: data.tags,
          })
          .eq('id', post.id);

        if (error) throw error;
      } else {
        // Create new post
        const { error } = await supabase.from('community_posts').insert({
          title: data.title,
          content: data.content,
          type: 'blog',
          user_id: session.session.user.id,
          city: data.city,
          guide_type: data.guide_type,
          tags: data.tags,
        });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      toast({
        title: 'Success',
        description: post?.id ? 'Blog post updated successfully' : 'Blog post created successfully',
      });
      setOpen(false);
      reset();
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: post?.id ? 'Failed to update blog post' : 'Failed to create blog post',
      });
      console.error('Error with blog post:', error);
    },
  });

  const onSubmit = (data: BlogFormData) => {
    saveBlogPost(data);
  };

  const title = watch('title');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col bg-[#F1F0FB]/95 backdrop-blur supports-[backdrop-filter]:bg-[#F1F0FB]/80">
        <DialogHeader className="border-b pb-4 border-primary/10">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Plane className="h-6 w-6 text-primary" />
            {post?.id ? 'Edit Travel Blog Post' : 'Share Your Travel Story'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-grow overflow-hidden pt-4">
          <div className="space-y-6 flex-shrink-0">
            <BlogPostFormFields 
              register={register}
              watch={watch}
              setValue={setValue}
            />
          </div>
          
          <div className="flex-grow overflow-hidden mt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <TabsList className="mb-4 flex-shrink-0 w-fit">
                <TabsTrigger value="edit" className="px-6">Write</TabsTrigger>
                <TabsTrigger value="preview" className="px-6">Preview</TabsTrigger>
              </TabsList>
              
              <div className="flex-grow overflow-auto">
                <TabsContent value="edit" className="h-full mt-0">
                  <div className="border rounded-lg shadow-sm h-full bg-white/95">
                    <BlogPostEditor 
                      initialContent={post?.content}
                      register={register}
                      setValue={setValue}
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="preview" className="h-full mt-0">
                  <div className="border rounded-lg p-8 h-full overflow-auto prose prose-lg max-w-none bg-white/95 shadow-sm">
                    <h1 className="text-primary font-montserrat mb-8">{title}</h1>
                    <div 
                      dangerouslySetInnerHTML={{ __html: watch('content') || '' }}
                      className="prose-headings:text-primary prose-p:text-[#6E59A5]"
                    />
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-primary/10 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="px-6"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isPending}
              className="px-6 bg-primary hover:bg-primary/90"
            >
              {isPending ? (post?.id ? 'Updating...' : 'Publishing...') : (post?.id ? 'Update' : 'Publish')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
